// Time-series chart used by the Data Explorer and the Trend widget.
//
// Built for long tests: the server already limits each series to ~1 000 buckets; on top of
// that the canvas renderer draws without animation or point symbols, applies LTTB sampling
// when a series is longer than the plot is wide, and updates are applied in place
// (`setOption` with merge) instead of re-creating the chart.
import { memo, useEffect, useRef, useState, type ReactNode } from 'react';
import classes from './TimeSeriesChart.module.css';
import { chartPalette } from '@web/app/theme';
import { eventText, formatDateTime24 } from '@web/lib/format';
import { layoutAxes, type AxisSide } from './axes';
import { echarts, type ECharts, type EChartsOption } from './echarts';

export interface ChartSeries {
  id: string;
  name: string;
  /** Unit symbol; series sharing a unit share a Y axis, unless `axis` says otherwise. */
  unit: string;
  points: Array<[number, number | null]>;
  color?: string;
  decimals?: number;
  step?: boolean;
  area?: boolean;
  hidden?: boolean;
  /** Line width in pixels (default 1.5). */
  width?: number;
  /** Rounding of the line between points, 0 (straight) to 1. Not applied to steps. */
  smooth?: number;
  /** Side of the Y axis; without it the side follows the unit. */
  axis?: AxisSide;
  /** Text for a value instead of the number (value mappings: 16 → "COOLDOWN"). */
  format?: (v: number) => string | null;
}

export interface ChartAnnotation {
  at: number;
  text: string;
  color?: string;
  /** 'logger' for SmarDac Logger events (their "[Activity]"-style prefix is not shown). */
  source?: string;
}

const escapeHtml = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

interface Props {
  series: ChartSeries[];
  annotations?: ChartAnnotation[];
  height?: number | string;
  /** Epoch ms; fixes the X axis to the requested range even when data is missing at the edges. */
  from?: number;
  to?: number;
  timeZone?: string;
  locale?: string;
  /** Called when the user drags a horizontal selection to zoom. */
  onZoom?: (from: number, to: number) => void;
  /** Charts with the same group share their crosshair. */
  group?: string;
  loading?: boolean;
  ariaLabel: string;
  showLegend?: boolean;
  /** Overrides of the theme (dashboard appearance settings). Keep the object stable between renders. */
  look?: ChartLook;
  /**
   * Actions offered by a right-click on the plot, at the moment under the pointer (e.g. "Ask the
   * supervisors", doc 14). None: the browser's own menu.
   */
  contextActions?: ChartContextAction[];
}

export interface ChartContextAction {
  key: string;
  label: string;
  icon?: ReactNode;
  onSelect: (at: number) => void;
}

export interface ChartLook {
  textColor?: string;
  /** Axis and grid lines. */
  lineColor?: string;
  fontFamily?: string;
  fontSize?: number;
  gridLines?: boolean;
}

function buildOption(p: Props): EChartsOption {
  const visible = p.series;
  // Up to four axes; further units share the last axis.
  const { axes, indexes } = layoutAxes(visible);
  const onLeft = axes.filter((a) => a.side === 'left').length;
  const onRight = axes.length - onLeft;
  const fmtNumber = (v: number, decimals = 2) =>
    new Intl.NumberFormat(p.locale ?? 'en', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(v);
  const fmtTime = (ms: number) =>
    new Intl.DateTimeFormat(p.locale ?? 'en', { dateStyle: 'medium', timeStyle: 'medium', timeZone: p.timeZone }).format(new Date(ms));

  // Colours follow the theme tokens, so charts read well on the dark theme too.
  const css = getComputedStyle(document.documentElement);
  const token = (name: string, fallback: string) => css.getPropertyValue(name).trim() || fallback;
  const line = p.look?.lineColor ?? token('--tl-border-strong', '#dcdee4');
  const faint = p.look?.lineColor ?? token('--tl-border', '#eef0f4');
  const muted = p.look?.textColor ?? token('--tl-text-muted', '#64748b');
  const body = p.look?.textColor ?? token('--tl-text-body', '#475569');
  const fontSize = p.look?.fontSize ?? 11;

  return {
    animation: false,
    color: [...chartPalette],
    textStyle: { fontFamily: p.look?.fontFamily ?? 'Inter, sans-serif', fontSize },
    grid: {
      // Room for the legend row and, below it, the unit names above each axis.
      top: p.showLegend === false ? 26 : 54,
      bottom: 28,
      left: 12 + Math.max(1, onLeft) * 46,
      right: 16 + onRight * 46,
      containLabel: false,
    },
    legend: p.showLegend === false ? { show: false } : { type: 'scroll', top: 4, left: 8, right: 8, itemWidth: 14, itemHeight: 8, textStyle: { fontSize, color: body } },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross', label: { show: false }, lineStyle: { color: '#94a3b8' } },
      confine: true,
      transitionDuration: 0,
      borderColor: '#e5e7eb',
      textStyle: { fontSize: 12, color: '#283c50' },
      formatter: (params) => {
        const items = Array.isArray(params) ? params : [params];
        if (items.length === 0) return '';
        const t = (items[0]!.value as [number, number])[0];
        const rows = items
          .map((it) => {
            const s = visible.find((x) => x.id === it.seriesId);
            const v = (it.value as [number, number | null])[1];
            const mapped = v === null || v === undefined ? null : (s?.format?.(v) ?? null);
            const text = v === null || v === undefined ? '—' : (mapped ?? `${fmtNumber(v, s?.decimals ?? 2)} ${s?.unit ?? ''}`);
            return `<div style="display:flex;justify-content:space-between;gap:16px"><span>${it.marker}${it.seriesName}</span><b style="font-variant-numeric:tabular-nums">${text}</b></div>`;
          })
          .join('');
        return `<div style="font-size:11px;color:#64748b;margin-bottom:4px">${fmtTime(t)}</div>${rows}`;
      },
    },
    toolbox: p.onZoom ? { show: false, feature: { dataZoom: { yAxisIndex: 'none' } } } : undefined,
    xAxis: {
      type: 'time',
      min: p.from,
      max: p.to,
      axisLine: { lineStyle: { color: line } },
      axisLabel: { color: muted, fontSize, hideOverlap: true },
      splitLine: { show: false },
    },
    yAxis: (axes.length ? axes : [{ unit: '', side: 'left' as const, rank: 0 }]).map(({ unit, side, rank }, i) => ({
      type: 'value',
      scale: true,
      name: unit,
      nameTextStyle: { color: muted, fontSize: 10, align: side === 'left' ? 'right' : 'left' },
      position: side,
      offset: rank * 46,
      axisLine: { show: true, lineStyle: { color: line } },
      axisLabel: { color: muted, fontSize: Math.max(8, fontSize - 1) },
      splitLine: { show: i === 0 && p.look?.gridLines !== false, lineStyle: { color: faint } },
    })),
    series: visible.map((s, i) => ({
      id: s.id,
      name: s.name,
      type: 'line',
      yAxisIndex: indexes[i] ?? 0,
      data: s.hidden ? [] : s.points,
      showSymbol: false,
      connectNulls: false,
      step: s.step ? 'end' : undefined,
      // Monotone: a rounded line never goes above or below the values it joins.
      smooth: s.step ? false : (s.smooth ?? 0),
      smoothMonotone: 'x',
      sampling: 'lttb',
      large: true,
      lineStyle: { width: s.width ?? 1.5 },
      areaStyle: s.area ? { opacity: 0.12 } : undefined,
      itemStyle: s.color ? { color: s.color } : undefined,
      emphasis: { disabled: true },
      // Sequence-log entries are drawn once, on the first series.
      markLine:
        i === 0 && p.annotations?.length
          ? {
              silent: false,
              symbol: 'none',
              animation: false,
              label: { show: false },
              lineStyle: { type: 'dashed', color: '#94a3b8', width: 1 },
              // Same look as the sequence log: "<b>date time (24 h)</b> : text". The text is escaped: it is HTML here.
              tooltip: { trigger: 'item', formatter: (param: unknown) => String((param as { data?: { name?: string } }).data?.name ?? '') },
              data: p.annotations.map((a) => ({
                xAxis: a.at,
                name: `<b>${escapeHtml(formatDateTime24(new Date(a.at).toISOString(), p.timeZone))}</b> : ${escapeHtml(eventText(a.text, a.source))}`,
                lineStyle: a.color ? { color: a.color } : undefined,
              })),
            }
          : undefined,
    })),
  };
}

/** Memoized: a parent that re-renders on every live tick does not redraw an unchanged chart. */
export const TimeSeriesChart = memo(function TimeSeriesChart(props: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const chart = useRef<ECharts | null>(null);
  const onZoom = useRef(props.onZoom);
  onZoom.current = props.onZoom;
  const actions = useRef(props.contextActions);
  actions.current = props.contextActions;
  /** The right-click menu: where it opens (px in the chart) and the moment under the pointer. */
  const [menu, setMenu] = useState<{ x: number; y: number; at: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    const instance = echarts.init(ref.current, undefined, { renderer: 'canvas' });
    chart.current = instance;
    const resize = new ResizeObserver(() => instance.resize());
    resize.observe(ref.current);
    // Drag to zoom: the brush result is handed to the page, which re-queries at full resolution.
    instance.on('datazoom', (e) => {
      const batch = (e as { batch?: Array<{ startValue?: number; endValue?: number }> }).batch?.[0];
      if (batch?.startValue !== undefined && batch.endValue !== undefined && batch.endValue > batch.startValue) {
        onZoom.current?.(Math.round(batch.startValue), Math.round(batch.endValue));
        // The page owns the range: clear the chart's own zoom so the next data fills the plot.
        instance.dispatchAction({ type: 'dataZoom', start: 0, end: 100 });
      }
    });
    // Right-click inside the plot: the moment under the pointer, for the chart's own menu.
    const el = ref.current;
    const onContext = (e: MouseEvent) => {
      if (!actions.current?.length) return;
      const box = el.getBoundingClientRect();
      const point: [number, number] = [e.clientX - box.left, e.clientY - box.top];
      if (!instance.containPixel({ gridIndex: 0 }, point)) return;
      // In the grid's coordinates: [time, value].
      const at = Number((instance.convertFromPixel({ gridIndex: 0 }, point) as number[] | undefined)?.[0]);
      if (!Number.isFinite(at)) return;
      e.preventDefault();
      setMenu({ x: point[0], y: point[1], at: Math.round(at) });
    };
    el.addEventListener('contextmenu', onContext);
    return () => {
      el.removeEventListener('contextmenu', onContext);
      resize.disconnect();
      instance.dispose();
      chart.current = null;
    };
  }, []);

  // The menu closes on Escape, on a click elsewhere and when the page scrolls.
  useEffect(() => {
    if (!menu) return;
    menuRef.current?.querySelector('button')?.focus();
    const close = () => setMenu(null);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    const onDown = (e: MouseEvent) => !menuRef.current?.contains(e.target as Node) && close();
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', close, true);
    };
  }, [menu]);

  useEffect(() => {
    const instance = chart.current;
    if (!instance) return;
    // `replaceMerge` keeps the instance and swaps only the series/axes that changed.
    instance.setOption(buildOption(props), { replaceMerge: ['series', 'yAxis'], lazyUpdate: true });
    if (props.onZoom) {
      instance.dispatchAction({ type: 'takeGlobalCursor', key: 'dataZoomSelect', dataZoomSelectActive: true });
    }
  });

  useEffect(() => {
    if (chart.current && props.group) {
      chart.current.group = props.group;
      echarts.connect(props.group);
    }
  }, [props.group]);

  const time = (ms: number) => new Intl.DateTimeFormat(props.locale ?? 'en', { dateStyle: 'medium', timeStyle: 'short', timeZone: props.timeZone }).format(new Date(ms));
  return (
    <div className={classes.wrap} style={{ height: props.height ?? 360 }}>
      <div
        ref={ref}
        role="img"
        aria-label={props.ariaLabel}
        style={{ width: '100%', height: '100%', opacity: props.loading ? 0.55 : 1, transition: 'opacity 0.15s' }}
      />
      {menu && props.contextActions?.length ? (
        <div ref={menuRef} role="menu" aria-label={time(menu.at)} className={classes.menu} style={{ left: menu.x, top: menu.y }}>
          <div className={classes.menuTitle}>{time(menu.at)}</div>
          {props.contextActions.map((a) => (
            <button
              key={a.key}
              type="button"
              role="menuitem"
              className={classes.menuItem}
              onClick={() => {
                setMenu(null);
                a.onSelect(menu.at);
              }}
            >
              {a.icon}
              <span>{a.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
});
